import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { format, parse } from "date-fns";

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
    fontFamily: "Helvetica",
    position: "relative",
  },
  header: {
    marginBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  headerSection: {
    flex: 1,
  },
  rightHeaderSection: {
    flex: 1,
    alignItems: 'flex-end',
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 5,
  },
  label: {
    fontWeight: "bold",
    fontSize: 10,
    marginRight: 5,
  },
  value: {
    fontSize: 10,
  },
  table: {
    marginTop: 10,
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderBottomColor: "#000",
    borderTopColor: "#000",
    backgroundColor: "#f0f0f0",
    padding: 6,
  },
  tableRow: {
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    padding: 8,
  },
  categoryRow: {
    flexDirection: "row",
    backgroundColor: "#f5f5f5",
    padding: 8,
    marginTop: 5,
  },
  sNo: { width: "5%" },
  itemName: { width: "35%" },
  au: { width: "10%" },
  quantity: { width: "15%", textAlign: "right"},
  stock: { width: "10%", textAlign: "right" },
  promisedDays: { width: "12%" },
  committedDate: { width: "13%" },
  headerText: {
    fontSize: 8,
    fontWeight: "bold",
  },
  cellText: {
    fontSize: 8,
  },
  remarksText: {
    fontSize: 8,
    marginTop: 4,
    marginLeft: "5%",
  },
  footerWrapper: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
  },
  footer: {
    fontSize: 8,
    textAlign: "center",
    color: "#666",
  },
  mainHeading: {
    fontSize: 14,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 10,
    textDecoration: 'underline',
  },
  status: {
    fontSize: 12,
    textAlign: "center",
    marginBottom: 15,
  },
  contentWrapper: {
    minHeight: "100%",
    paddingBottom: 50,
  }
});

export const IndentPDF = ({ data }) => {
  const formatDate = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    const date = parse(dateString, "yyyy-MM-dd", new Date());
    return isNaN(date.getTime()) ? "N/A" : format(date, "dd/MM/yy");
  };

  const formatCreationDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = parse(dateString, "MMMM dd, yyyy", new Date());
      return format(date, "dd/MM/yy");
    } catch {
      return dateString;
    }
  };

  const indents = Array.isArray(data) ? data : [data];
  const currentDateTime = format(new Date(), "dd/MM/yyyy HH:mm:ss");

  const Footer = () => (
    <View style={styles.footerWrapper} fixed>
      <View style={styles.footer}>
        <Text>© Confident Indent Management System - {currentDateTime}</Text>
      </View>
    </View>
  );

  return (
    <Document>
      {indents.map((indent, pageIndex) => {
        let lastCategory = null;

        return (
          <Page key={pageIndex} size="A4" style={styles.page}>
            <View style={styles.contentWrapper}>
              <Text style={styles.mainHeading}>Indent Request</Text>
              <Text style={styles.status}>{indent.status || indent.Status}</Text>

              <View style={styles.header}>
                <View style={styles.headerSection}>
                  <View style={styles.infoRow}>
                    <Text style={styles.label}>Indent #:</Text>
                    <Text style={styles.value}>{indent.indentId}</Text>
                  </View>
                  <View style={styles.infoRow}>
                    <Text style={styles.label}>Creation Date:</Text>
                    <Text style={styles.value}>{formatCreationDate(indent.creationDate)}</Text>
                  </View>
                  <View style={styles.infoRow}>
                    <Text style={styles.label}>Status:</Text>
                    <Text style={styles.value}>{indent.status}</Text>
                  </View>
                </View>
                <View style={styles.rightHeaderSection}>
                  <View style={styles.infoRow}>
                    <Text style={styles.label}>Project Name:</Text>
                    <Text style={styles.value}>{indent.projectName}</Text>
                  </View>
                  <View style={styles.infoRow}>
                    <Text style={styles.label}>Task Scheduled:</Text>
                    <Text style={styles.value}>{indent.taskScheduled}</Text>
                  </View>
                  <View style={styles.infoRow}>
                    <Text style={styles.label}>Indent Type:</Text>
                    <Text style={styles.value}>{indent.invoicingType}</Text>
                  </View>
                </View>
              </View>

              <View style={styles.table}>
                <View style={styles.tableHeader}>
                  <Text style={[styles.sNo, styles.headerText]}>S.No</Text>
                  <Text style={[styles.itemName, styles.headerText]}>Item Name</Text>
                  <Text style={[styles.au, styles.headerText]}>AU</Text>
                  <Text style={[styles.quantity, styles.headerText]}>Quantity</Text>
                  <Text style={[styles.stock, styles.headerText]}>Stock</Text>
                  <Text style={[styles.promisedDays, styles.headerText]}>Promised Days</Text>
                  <Text style={[styles.committedDate, styles.headerText]}>Committed Date</Text>
                </View>

                {indent.items.map((item, index) => {
                  const showCategory = item.category !== lastCategory;
                  lastCategory = item.category;

                  return (
                    <View key={index}>
                      {showCategory && item.category && (
                        <View style={styles.categoryRow}>
                          <Text
                            style={[
                              { width: "100%" },
                              styles.cellText,
                              { fontWeight: "bold" },
                            ]}
                          >
                            Category: {item.category}
                          </Text>
                        </View>
                      )}
                      <View style={styles.tableRow}>
                        <View style={{ flexDirection: "row" }}>
                          <Text style={[styles.sNo, styles.cellText]}>{index + 1}</Text>
                          <Text style={[styles.itemName, styles.cellText]}>{item.name}</Text>
                          <Text style={[styles.au, styles.cellText]}>{item.au}</Text>
                          <Text style={[styles.quantity, styles.cellText, "textAlign: 'right'"]}>{item.quantity}</Text>
                          <Text style={[styles.stock, styles.cellText]}>{item.stock}</Text>
                          <Text style={[styles.promisedDays, styles.cellText]}>{item.promisedDays}</Text>
                          <Text style={[styles.committedDate, styles.cellText]}>{formatDate(item.committedDate)}</Text>
                        </View>
                        {item.description && item.description.trim() !== "__" && (
                          <Text style={styles.remarksText}>Remarks: {item.description}</Text>
                        )}
                      </View>
                    </View>
                  );
                })}
              </View>
            </View>
            <Footer />
          </Page>
        );
      })}
    </Document>
  );
};